// Create a Providers component to wrap your application with all the components requiring 'use client', such as next-nprogress-bar or your different contexts...
"use client"

import { AppProgressBar as ProgressBar } from "next-nprogress-bar"
import { useEffect, type ReactNode } from "react"
import { AuthProvider } from "@/modules/auth"

const CHUNK_RELOAD_SESSION_KEY = "yoho-chunk-load-reloaded"

function isChunkLoadFailure(error: unknown) {
  if (!error || typeof error !== "object") return false
  const record = error as { name?: unknown; message?: unknown }
  const name = typeof record.name === "string" ? record.name : ""
  const message = typeof record.message === "string" ? record.message : ""
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  )
}

function useChunkLoadRecovery() {
  useEffect(() => {
    const reloadOnce = () => {
      if (window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1") {
        return
      }
      window.sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, "1")
      window.location.reload()
    }

    const handleError = (event: ErrorEvent) => {
      if (
        isChunkLoadFailure(event.error) ||
        isChunkLoadFailure(event.message)
      ) {
        reloadOnce()
      }
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadFailure(event.reason)) reloadOnce()
    }

    window.addEventListener("error", handleError)
    window.addEventListener("unhandledrejection", handleUnhandledRejection)

    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleUnhandledRejection)
    }
  }, [])
}

type ProvidersProps = {
  children: ReactNode
  initialAuthStatus?: "authenticated" | "unauthenticated"
}

const Providers = ({ children, initialAuthStatus }: ProvidersProps) => {
  useChunkLoadRecovery()

  return (
    <AuthProvider initialStatus={initialAuthStatus}>
      <>
        {children}
        <ProgressBar
          height="2px"
          color="#f97316"
          options={{ showSpinner: false }}
          shallowRouting
          disableSameURL
        />
      </>
    </AuthProvider>
  )
}

export default Providers
